import React, { useCallback } from "react";
import { useAuth } from "@clerk/nextjs";
import { usePremiumFeatureModal } from "features/Subscription/components";
import { SNAPSHOT_CREDITS_PER_MONTH_FREE_USER } from "constants/config";
import { SubscriptionPlan } from "enums/subscription-plans";
import { SubscriptionPlanIcon } from "features/Pricing/utils";
import useCredits from "features/User/hooks/useCredits";
import pluralize from "helpers/pluralize";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { CreditedFeature } from "store/slices/subscription";
import CardTooltip from "../CardTooltip";
interface StudySnapshotTooltipProps {
  snapshotType:
    | CreditedFeature.STUDY_SNAPSHOT
    | CreditedFeature.CONSENSUS_SNAPSHOT;
  onClickUpgradePremium?: (e: React.MouseEvent<HTMLElement>) => void;
}

const snapshotTypeToTooltipLabels = {
  [CreditedFeature.STUDY_SNAPSHOT]: "tooltips.study-snapshot",
  [CreditedFeature.CONSENSUS_SNAPSHOT]: "tooltips.consensus-snapshot",
};

/**
 * @component SnapshotTooltip
 * @description Tooltip explaining Study Snapshot or Consensus Snapshot feature and cost.
 *
 * @param {CreditedFeature.STUDY_SNAPSHOT | CreditedFeature.CONSENSUS_SNAPSHOT} props.snapshotType - The type of snapshot to display tooltip for.
 * @param {Function} props.onClickUpgradePremium - Optional callback to run when the upgrade to premium button is clicked.
 */
const SnapshotTooltip = ({
  snapshotType,
  onClickUpgradePremium,
}: StudySnapshotTooltipProps) => {
  const [tooltipLabels, creditsLabels] = useLabels(
    snapshotTypeToTooltipLabels[snapshotType],
    "tooltips.credits"
  );

  const { isSignedIn, isLoaded } = useAuth();
  const notSignedIn = isLoaded && isSignedIn === false;

  const { isPremium, snapshotCreditsRemaining, creditRefreshDate } =
    useCredits();
  const { openPremiumFeatureModal } = usePremiumFeatureModal();
  const handleClickUpgradePremium = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      openPremiumFeatureModal({
        feature: snapshotType,
        actionSource: MixpanelActionSource.StudySnapshot,
      });
      onClickUpgradePremium && onClickUpgradePremium(e);
    },
    [openPremiumFeatureModal, onClickUpgradePremium, snapshotType]
  );

  const showCreditsRemaining = !isPremium && !notSignedIn;

  const hint =
    showCreditsRemaining &&
    creditsLabels["credits-left"].replace(
      "{CREDITS_REMAINING}",
      snapshotCreditsRemaining
    );

  return (
    <CardTooltip>
      <CardTooltip.Header
        title={tooltipLabels["title"]}
        iconComponent={<i className="icon-table-2 text-2xl" />}
        hint={hint}
      />
      <CardTooltip.Description content={tooltipLabels["content"]} />
      {showCreditsRemaining && (
        <CardTooltip.Hint
          content={pluralize(creditsLabels["snapshots-left"], {
            CREDITS_REMAINING: snapshotCreditsRemaining,
            MAX_CREDIT: SNAPSHOT_CREDITS_PER_MONTH_FREE_USER,
            RENEW_DATE: creditRefreshDate,
          })}
        />
      )}
      {showCreditsRemaining && (
        <CardTooltip.Button
          label={creditsLabels["upgrade-to-premium"]}
          icon={<SubscriptionPlanIcon plan={SubscriptionPlan.PREMIUM} />}
          onClick={handleClickUpgradePremium}
          data-testid="snapshot-btn-upgrade-premium"
        />
      )}
    </CardTooltip>
  );
};

export default SnapshotTooltip;
